import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Calendar, Link2, Check, X, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

/**
 * GoogleCalendarSyncCard - Handles Google Calendar integration for services
 * Shows sync status and allows connecting/disconnecting Google Calendar
 */
const GoogleCalendarSyncCard = ({ formData, handleChange }) => {
  const { apiCall, user } = useAuth();
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [saStatus, setSaStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const isEnabled = formData.google_calendar_sync || false;

  useEffect(() => {
    checkCalendarStatus();
  }, []);

  const checkCalendarStatus = async () => {
    setLoading(true);
    try {
      const [status, sa] = await Promise.all([
        apiCall('/api/calendar/status'),
        apiCall('/api/calendar/sa-status').catch(() => ({ configured: false })),
      ]);
      setCalendarStatus(status);
      setSaStatus(sa);
    } catch (error) {
      console.error('Failed to check calendar status:', error);
      setCalendarStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToggle = (enabled) => {
    if (enabled && !calendarStatus?.connected && !saStatus?.configured) {
      toast({
        title: 'Connect Google Calendar First',
        description: 'Please connect your Google Calendar before enabling sync.',
        variant: 'destructive'
      });
      return;
    }
    handleChange('google_calendar_sync', enabled);
  };

  const handleConnectCalendar = async () => {
    setIsConnecting(true);
    try {
      // Get the OAuth URL from backend
      const { auth_url } = await apiCall('/api/calendar/connect');
      
      if (auth_url) {
        // Open Google OAuth in a popup
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          auth_url,
          'google-calendar-auth',
          `width=${width},height=${height},left=${left},top=${top},popup=1`
        );

        // Poll for popup close
        const pollTimer = setInterval(() => {
          if (popup?.closed) {
            clearInterval(pollTimer);
            setIsConnecting(false);
            checkCalendarStatus();
          }
        }, 500);
      }
    } catch (error) {
      console.error('Failed to connect calendar:', error);
      toast({
        title: 'Connection Failed',
        description: error.message || 'Failed to connect to Google Calendar',
        variant: 'destructive'
      });
      setIsConnecting(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    try {
      await apiCall('/api/calendar/disconnect', { method: 'POST' });
      setCalendarStatus({ connected: false });
      handleChange('google_calendar_sync', false);
      toast({
        title: 'Calendar Disconnected',
        description: 'Google Calendar has been disconnected',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect calendar',
        variant: 'destructive'
      });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Calendar Sync
        </CardTitle>
        <CardDescription>
          Sync bookings with your Google Calendar
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking calendar status...
          </div>
        ) : (
          <>
            {/* Connection Status */}
            <div className="p-3 rounded-lg bg-gray-50 border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${
                    calendarStatus?.connected 
                      ? 'bg-green-100' 
                      : 'bg-gray-100'
                  }`}>
                    <Calendar className={`h-4 w-4 ${
                      calendarStatus?.connected 
                        ? 'text-green-600' 
                        : 'text-gray-400'
                    }`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {calendarStatus?.connected 
                        ? 'Google Calendar Connected' 
                        : 'Not Connected'
                      }
                    </p>
                    {calendarStatus?.email && (
                      <p className="text-xs text-gray-500">
                        {calendarStatus.email}
                      </p>
                    )}
                  </div>
                </div>
                {calendarStatus?.connected ? (
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                    <Check className="h-3 w-3 mr-1" />
                    Connected
                  </Badge>
                ) : (
                  <Button 
                    size="sm" 
                    onClick={handleConnectCalendar}
                    disabled={isConnecting}
                    data-testid="connect-calendar-btn"
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Link2 className="h-4 w-4 mr-2" />
                        Connect
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Sync Toggle */}
            {(calendarStatus?.connected || saStatus?.configured) && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Link2 className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <Label className="font-medium text-blue-900">
                      Enable Calendar Sync
                    </Label>
                    <p className="text-xs text-blue-700">
                      {calendarStatus?.connected
                        ? 'Automatically sync bookings to your calendar'
                        : 'Sync via platform calendar (connect yours for personal access)'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={handleSyncToggle}
                  data-testid="calendar-sync-switch"
                />
              </div>
            )}

            {/* Disconnect Option */}
            {calendarStatus?.connected && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleDisconnectCalendar}
                data-testid="disconnect-calendar-btn"
              >
                <X className="h-4 w-4 mr-2" />
                Disconnect Calendar
              </Button>
            )}

            {/* Info */}
            <p className="text-xs text-gray-500">
              When enabled, new bookings will automatically create events in your Google Calendar.
            </p>

            {/* Platform Calendar (Service Account) Status */}
            {saStatus?.configured && (
              <div className="flex items-center gap-2 p-2 rounded-md bg-emerald-50 border border-emerald-200">
                <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                <p className="text-xs text-emerald-700">
                  <span className="font-medium">Platform Calendar active</span> — bookings sync even without your personal Google sign-in.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GoogleCalendarSyncCard;
